import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from "@tanstack/react-query";
import React, { useEffect, useState } from "react";
import {
  list,
  addHotel,
  updateHotel,
  deleteHotel,
} from "../../services/hotelService";

import FilePreview from "../FilePreview/FilePreview";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import Dialog from "../dialog/Dialog";
import Toster from "../common/Toster";
import Pagination from "../common/Pagination";
import {
  AdminTable,
  AdminTableHead,
  AdminTableTh,
  AdminTableBody,
  AdminTableRow,
  AdminTableCell,
  AdminTableFooter,
  TableSearchInput,
  TableEmptyRow,
  EditActionButton,
  DeleteActionButton,
  TableActionsCell,
  StatusBadge,
  getStatusVariant,
} from "../common/adminTable";
import { buildPaginationMeta } from "../../utils/clientPagination";
import {
  AdminPageLayout,
  AdminPageHeader,
  AdminPageCard,
  AdminPageCardToolbar,
} from "../common/AdminPageLayout";
import { SUPPLIER_PAGE_META } from "../common/supplierPageMeta";
import { getTableEmptyProps } from "../common/supplierTableEmpty";
import {
  SupplierAddButton,
  SupplierFilterToolbar,
  SupplierFilterField,
  SupplierPageLoading,
} from "../common/supplierPageUi";
import ImageInput from "../common/ImageInput";

const Hotels = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedHotel, setSelectedHotel] = useState(null);

  const [hotelName, setHotelName] = useState("");
  const [hotelLocation, setHotelLocation] = useState("");
  const [hotelDestination, setHotelDestination] = useState("");
  const [hotelWebsite, setHotelWebsite] = useState("");
  const [hotelStatus, setHotelStatus] = useState(1);
  const [hotelImage, setHotelImage] = useState(null);
  const [errors, setErrors] = useState({});
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const queryClient = useQueryClient();
  const [isActiveLoading, setIsActiveLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const pageMeta = SUPPLIER_PAGE_META.hotels;

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(1);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const { data, isLoading, error } = useQuery({
    queryKey: ["masterHotel", currentPage, debouncedSearchTerm],
    queryFn: () => list(currentPage, debouncedSearchTerm),
    placeholderData: keepPreviousData,
  });

  const clearSearch = () => {
    setSearchTerm("");
    setDebouncedSearchTerm("");
    setCurrentPage(1);
  };

  const showSuccessMessage = (message) => {
    setSuccessMessage(message);
    setTimeout(() => {
      setSuccessMessage("");
    }, 3000);
  };

  const showErrorMessage = (message) => {
    setErrorMessage(message);
    setTimeout(() => {
      setErrorMessage("");
    }, 3000);
  };

  const addMutation = useMutation({
    mutationFn: addHotel,
    onSuccess: (success) => {
      queryClient.invalidateQueries(["masterHotel"]);
      setAddDialogOpen(false);
      clearFormData();
      setIsActiveLoading(false);
      showSuccessMessage(success.response?.data?.message || "Hotel added successfully");
    },
    onError: (err) => {
      setIsActiveLoading(false);
      showErrorMessage(err.response?.data?.message || "Something went wrong");
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, formData }) => updateHotel(id, formData),
    onSuccess: (success) => {
      queryClient.invalidateQueries(["masterHotel"]);
      setEditDialogOpen(false);
      clearFormData();
      setSelectedHotel(null);
      setIsActiveLoading(false);
      showSuccessMessage(success.response?.data?.message || "Hotel updated successfully");
    },
    onError: (err) => {
      setIsActiveLoading(false);
      showErrorMessage(err.response?.data?.message || "Something went wrong");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteHotel,
    onSuccess: (success) => {
      queryClient.invalidateQueries(["masterHotel"]);
      setDeleteDialogOpen(false);
      setSelectedHotel(null);
      setIsActiveLoading(false);
      showSuccessMessage(success.response?.data?.message || "Hotel deleted successfully");
    },
    onError: (err) => {
      setIsActiveLoading(false);
      showErrorMessage(err.response?.data?.message || "Something went wrong");
    },
  });

  const clearFormData = () => {
    setHotelName("");
    setHotelLocation("");
    setHotelDestination("");
    setHotelWebsite("");
    setHotelStatus(1);
    setHotelImage(null);
    setErrors({});
  };

  const validateForm = () => {
    const newErrors = {};
    if (!hotelName) newErrors.hotelName = "Hotel name is required";
    if (!hotelLocation) newErrors.hotelLocation = "Location is required";
    if (!hotelDestination) newErrors.hotelDestination = "Destination is required";
    if (!hotelImage && !selectedHotel) newErrors.hotelImage = "Hotel image is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAddClick = () => {
    clearFormData();
    setAddDialogOpen(true);
  };

  const handleEditClick = (id) => {
    const hotel = hotels.find((h) => h.id === id);
    setSelectedHotel(hotel);
    setHotelName(hotel.hotel_name || "");
    setHotelLocation(hotel.location || "");
    setHotelDestination(hotel.destination || "");
    setHotelWebsite(hotel.website || "");
    setHotelStatus(hotel.status ?? 1);
    setHotelImage(null);
    setErrors({});
    setEditDialogOpen(true);
  };

  const handleDeleteClick = (hotel) => {
    setSelectedHotel(hotel);
    setDeleteDialogOpen(true);
  };

  const handleSaveHotel = async () => {
    setIsActiveLoading(true);
    if (validateForm()) {
      const formData = new FormData();
      formData.append("hotel_name", hotelName);
      formData.append("location", hotelLocation);
      formData.append("destination", hotelDestination);
      formData.append("website", hotelWebsite);
      formData.append("status", hotelStatus);
      if (hotelImage) {
        formData.append("image", hotelImage);
      }
      addMutation.mutate(formData);
    } else {
      setIsActiveLoading(false);
    }
  };

  const handleUpdateHotel = async () => {
    if (validateForm()) {
      const formData = new FormData();
      formData.append("hotel_name", hotelName);
      formData.append("location", hotelLocation);
      formData.append("destination", hotelDestination);
      formData.append("website", hotelWebsite);
      formData.append("status", hotelStatus);
      if (hotelImage) {
        formData.append("image", hotelImage);
      }

      updateMutation.mutate({ id: selectedHotel.id, formData });
    } else {
      setIsActiveLoading(false);
    }
  };

  const handleDeleteHotel = () => {
    setIsActiveLoading(true);
    deleteMutation.mutate(selectedHotel.id);
  };

  const hotels = Array.isArray(data?.data?.data) ? data.data.data : [];
  const current_page = data?.data?.current_page ?? 1;
  const last_page = data?.data?.last_page ?? 1;
  const total = data?.data?.total ?? 0;
  const per_page = data?.data?.per_page ?? 10;

  const pagination = buildPaginationMeta({
    currentPage: current_page,
    lastPage: last_page,
    perPage: per_page,
    total,
  });

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  return (
    <AdminPageLayout>
      <AdminPageHeader
        title={pageMeta.title}
        subtitle={pageMeta.subtitle}
        actions={<SupplierAddButton onClick={handleAddClick}>Add Hotel</SupplierAddButton>}
      />

      <AdminPageCard>
        <AdminPageCardToolbar>
          <SupplierFilterToolbar showReset={!!searchTerm} onReset={clearSearch}>
            <SupplierFilterField label="Search">
              <TableSearchInput
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onClear={clearSearch}
                placeholder="Search hotels..."
                className="mt-1 w-full sm:w-full"
              />
            </SupplierFilterField>
          </SupplierFilterToolbar>
        </AdminPageCardToolbar>

        {isLoading ? (
          <SupplierPageLoading message="Loading hotels..." />
        ) : error ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <i className="fi fi-rr-exclamation text-red-500 text-4xl mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Error loading hotels
              </h3>
              <p className="text-gray-600">{error.message}</p>
            </div>
          </div>
        ) : (
          <AdminTable
            className="shadow-none rounded-none"
            footer={
              <AdminTableFooter>
                <Pagination pagination={pagination} onPageChange={handlePageChange} />
              </AdminTableFooter>
            }
          >
            <AdminTableHead>
              <AdminTableTh>#</AdminTableTh>
              <AdminTableTh>Images</AdminTableTh>
              <AdminTableTh>Hotels</AdminTableTh>
              <AdminTableTh>Locations</AdminTableTh>
              <AdminTableTh>Destinations</AdminTableTh>
              <AdminTableTh>Websites</AdminTableTh>
              <AdminTableTh>Stage</AdminTableTh>
              <AdminTableTh align="right">Actions</AdminTableTh>
            </AdminTableHead>
            <AdminTableBody>
              {hotels.length === 0 ? (
                <TableEmptyRow
                  colSpan={8}
                  {...getTableEmptyProps("hotels", {
                    onAdd: handleAddClick,
                    searchQuery: debouncedSearchTerm,
                    onClearSearch: clearSearch,
                    clearFiltersLabel: "Clear search",
                  })}
                />
              ) : (
                hotels.map((hotel, index) => (
                  <AdminTableRow key={hotel.id}>
                    <AdminTableCell>
                      {(current_page - 1) * per_page + index + 1}
                    </AdminTableCell>
                    <AdminTableCell>
                      <img
                        src={hotel.image_url}
                        alt="Hotel preview"
                        className="h-8 w-8 object-cover rounded-md"
                      />
                    </AdminTableCell>
                    <AdminTableCell className="font-medium">
                      {hotel.hotel_name}
                    </AdminTableCell>
                    <AdminTableCell>{hotel.location}</AdminTableCell>
                    <AdminTableCell>{hotel.destination}</AdminTableCell>
                    <AdminTableCell nowrap={false} className="max-w-xs truncate">
                      {hotel.website}
                    </AdminTableCell>
                    <AdminTableCell>
                      <StatusBadge variant={getStatusVariant(hotel.status)}>
                        {hotel.status === 1 ? "Active" : "Inactive"}
                      </StatusBadge>
                    </AdminTableCell>
                    <TableActionsCell>
                      <EditActionButton
                        showLabel={false}
                        onClick={() => handleEditClick(hotel.id)}
                      />
                      <DeleteActionButton
                        showLabel={false}
                        onClick={() => handleDeleteClick(hotel)}
                      />
                    </TableActionsCell>
                  </AdminTableRow>
                ))
              )}
            </AdminTableBody>
          </AdminTable>
        )}
      </AdminPageCard>

      <Dialog
        open={addDialogOpen}
        onClose={() => {
          setAddDialogOpen(false);
          clearFormData();
        }}
        title={
          <div className="flex items-center gap-2">
            <i className="fi fi-rr-add"></i>
            Create Hotel
          </div>
        }
        primaryButtonText="Save"
        primaryButtonAction={handleSaveHotel}
        closeButtonText="Close"
        closeButtonAction={() => {
          setAddDialogOpen(false);
          clearFormData();
        }}
        isLoading={isActiveLoading}
      >
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Destination <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              className={`placeholder:text-sm w-full px-3 py-1 border ${
                errors.hotelDestination ? "border-red-500" : "border-gray-300"
              } rounded-lg focus:outline-none focus:border-primary focus:ring-none`}
              placeholder="Enter destination"
              value={hotelDestination}
              onChange={(e) => setHotelDestination(e.target.value)}
            />
            {errors.hotelDestination && (
              <p className="text-red-500 text-xs mt-1">
                {errors.hotelDestination}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Location <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              className={`placeholder:text-sm w-full px-3 py-1 border ${
                errors.hotelLocation ? "border-red-500" : "border-gray-300"
              } rounded-lg focus:outline-none focus:border-primary focus:ring-none`}
              placeholder="Enter location"
              value={hotelLocation}
              onChange={(e) => setHotelLocation(e.target.value)}
            />
            {errors.hotelLocation && (
              <p className="text-red-500 text-xs mt-1">
                {errors.hotelLocation}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Hotel <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              className={`placeholder:text-sm w-full px-2 py-1 border ${
                errors.hotelName ? "border-red-500" : "border-gray-300"
              } rounded-lg focus:outline-none focus:border-primary focus:ring-none`}
              placeholder="Enter hotel name"
              value={hotelName}
              onChange={(e) => setHotelName(e.target.value)}
            />
            {errors.hotelName && (
              <p className="text-red-500 text-xs mt-1">{errors.hotelName}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Website
            </label>
            <input
              type="text"
              className={`placeholder:text-sm w-full px-3 py-1 border ${
                errors.hotelWebsite ? "border-red-500" : "border-gray-300"
              } rounded-lg focus:outline-none focus:border-primary focus:ring-none`}
              placeholder="Enter website URL"
              value={hotelWebsite}
              onChange={(e) => setHotelWebsite(e.target.value)}
            />
            {errors.hotelWebsite && (
              <p className="text-red-500 text-xs mt-1">{errors.hotelWebsite}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status <span className="text-red-500">*</span>
            </label>
            <select
              className="placeholder:text-sm w-full px-3 py-1 border border-gray-300 rounded-lg focus:outline-none focus:border-primary focus:ring-none"
              value={hotelStatus}
              onChange={(e) => setHotelStatus(Number(e.target.value))}
            >
              <option value={1}>Active</option>
              <option value={0}>Inactive</option>
            </select>
          </div>

          <div>
            <ImageInput
              onChange={setHotelImage}
              error={errors.hotelImage}
              label="Hotel Image"
              showLabel={true}
              required={true}
              className=""
            />
            {errors.hotelImage && (
              <p className="text-red-500 text-xs mt-1">{errors.hotelImage}</p>
            )}
          </div>
        </div>

        <div className="mt-3">
          <label className="block font-bold text-sm text-gray-700 mb-1">
            Attachment preview
          </label>
          {hotelImage && (
            <FilePreview
              file={hotelImage}
              fileUrl={URL.createObjectURL(hotelImage)}
              label="Hotel Image"
              onDelete={() => setHotelImage(null)}
              isImage={true}
            />
          )}
        </div>
      </Dialog>

      <Dialog
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          clearFormData();
          setSelectedHotel(null);
        }}
        title={
          <div className="flex items-center gap-2">
            <i className="fi fi-rr-pencil text-[16px]"></i>
            Update Hotel
          </div>
        }
        primaryButtonText="Update"
        primaryButtonAction={handleUpdateHotel}
        closeButtonText="Close"
        closeButtonAction={() => {
          setEditDialogOpen(false);
          clearFormData();
          setSelectedHotel(null);
        }}
        isLoading={isActiveLoading}
      >
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Destination <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              className={`placeholder:text-sm w-full px-3 py-1 border ${
                errors.hotelDestination ? "border-red-500" : "border-gray-300"
              } rounded-lg focus:outline-none focus:border-primary focus:ring-none`}
              placeholder="Enter destination"
              value={hotelDestination}
              onChange={(e) => setHotelDestination(e.target.value)}
            />
            {errors.hotelDestination && (
              <p className="text-red-500 text-xs mt-1">
                {errors.hotelDestination}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Location <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              className={`placeholder:text-sm w-full px-3 py-1 border ${
                errors.hotelLocation ? "border-red-500" : "border-gray-300"
              } rounded-lg focus:outline-none focus:border-primary focus:ring-none`}
              placeholder="Enter location"
              value={hotelLocation}
              onChange={(e) => setHotelLocation(e.target.value)}
            />
            {errors.hotelLocation && (
              <p className="text-red-500 text-xs mt-1">
                {errors.hotelLocation}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Hotel <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              className={`placeholder:text-sm w-full px-3 py-1 border ${
                errors.hotelName ? "border-red-500" : "border-gray-300"
              } rounded-lg focus:outline-none focus:border-primary focus:ring-none`}
              placeholder="Enter hotel name"
              value={hotelName}
              onChange={(e) => setHotelName(e.target.value)}
            />
            {errors.hotelName && (
              <p className="text-red-500 text-xs mt-1">{errors.hotelName}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Website
            </label>
            <input
              type="text"
              className={`placeholder:text-sm w-full px-3 py-1 border ${
                errors.hotelWebsite ? "border-red-500" : "border-gray-300"
              } rounded-lg focus:outline-none focus:border-primary focus:ring-none`}
              placeholder="Enter website URL"
              value={hotelWebsite}
              onChange={(e) => setHotelWebsite(e.target.value)}
            />
            {errors.hotelWebsite && (
              <p className="text-red-500 text-xs mt-1">{errors.hotelWebsite}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status <span className="text-red-500">*</span>
            </label>
            <select
              className="placeholder:text-sm w-full px-3 py-1 border border-gray-300 rounded-lg focus:outline-none focus:border-primary focus:ring-none"
              value={hotelStatus}
              onChange={(e) => setHotelStatus(Number(e.target.value))}
            >
              <option value={1}>Active</option>
              <option value={0}>Inactive</option>
            </select>
          </div>

          <div>
            <ImageInput
              onChange={setHotelImage}
              error={errors.hotelImage}
              label="Hotel Image"
              showLabel={true}
              required={true}
              className=""
              existingImage={selectedHotel?.image_url}
            />
            {errors.hotelImage && (
              <p className="text-red-500 text-xs mt-1">{errors.hotelImage}</p>
            )}
          </div>
        </div>

        <div className="mt-3">
          <label className="block font-bold text-sm text-gray-700 mb-1">
            Attachment preview
          </label>
          {hotelImage ? (
            <FilePreview
              file={hotelImage}
              width="w-[100px]"
              height="h-[100px]"
              label="New Image"
              onDelete={() => setHotelImage(null)}
              isImage={true}
            />
          ) : selectedHotel && selectedHotel.image_url ? (
            <FilePreview
              file={selectedHotel.image_url}
              width="w-[100px]"
              height="h-[100px]"
              label="Current Image"
              isImage={true}
            />
          ) : (
            <div className="text-sm text-gray-500">No image selected</div>
          )}
        </div>
      </Dialog>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        title={
          <div className="flex items-center gap-2">
            <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
            Delete Hotel
          </div>
        }
        primaryButtonText="Delete"
        primaryButtonAction={handleDeleteHotel}
        closeButtonText="Cancel"
        closeButtonAction={() => setDeleteDialogOpen(false)}
        isLoading={isActiveLoading}
      >
        <div className="mt-2">
          <p className="text-sm text-gray-500">
            Are you sure you want to delete this hotel? This action cannot be
            undone.
          </p>
        </div>
      </Dialog>

      {successMessage && (
        <Toster message={successMessage} type="success" onClose={() => setSuccessMessage("")} />
      )}
      {errorMessage && (
        <Toster message={errorMessage} type="error" onClose={() => setErrorMessage("")} />
      )}
    </AdminPageLayout>
  );
};

export default Hotels;
